import React, { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { RootState } from 'constants/interfaces'
import { Users, Calendar, Clock } from 'lucide-react'
import dayjs from 'dayjs'
import { StepType, useFormStepContext, TaskAudience } from 'components/TasksV2/TaskCreation/FormStepContext'
import { OFFSET_HALFWAY, OFFSET_WHEN_DONE } from '../../model/submission'
import { useAiCreationText } from '../../hooks/useAiCreationText'
import styles from './styles.module.css'

const MAX_AUDIENCE_SHOWN = 3

const TaskSummary: React.FC = () => {
  const { formData } = useFormStepContext()
  const {
    groups: configGroups,
    retailUsersObject: configUsers,
    tags: configTags,
  } = useSelector((state: RootState) => state.config.config)
  const t = useAiCreationText()

  const settings = (formData as any)?.[StepType.TASK_SETTINGS]
  const timeFrameData = (formData as any)?.[StepType.TIME_FRAME_SELECTION]
  const { audience, ai_schedule, from_ts: settingsFromTs, deadline: settingsDeadline } = settings || {}
  const { from_ts = settingsFromTs, deadline = settingsDeadline } = timeFrameData || {}
  const { groups = [], users = [], tags = [] } = (audience as TaskAudience) || {}
  const offsetMinutes: number | null = ai_schedule?.offsetMinutes ?? 15

  const audienceText = useMemo(() => {
    const groupNames = (groups as string[]).map((id) => configGroups?.[id]?.name).filter(Boolean) as string[]

    const userNames = (users as string[])
      .map((id) => {
        const u = configUsers?.[id]
        if (!u) return ''
        return `${u.first_name || ''} ${u.last_name || ''}`.trim()
      })
      .filter(Boolean) as string[]

    const tagNames = (tags as string[])
      .map((id) => configTags?.find((tag) => tag.sk === id)?.name || '')
      .filter(Boolean) as string[]

    const total = (groups?.length || 0) + (users?.length || 0) + (tags?.length || 0)
    if (total === 0) return '—'

    const allNames = [...groupNames, ...userNames, ...tagNames]
    if (allNames.length === 0) return `${total} ${t.audience}`

    const shown = allNames.slice(0, MAX_AUDIENCE_SHOWN)
    const remaining = total - shown.length
    return remaining > 0 ? `${shown.join(', ')} +${remaining}` : shown.join(', ')
  }, [groups, users, tags, configGroups, configUsers, configTags, t.audience])

  const briefingText = useMemo(() => {
    if (offsetMinutes === OFFSET_HALFWAY) return t.halfway
    if (offsetMinutes === OFFSET_WHEN_DONE) return t.whenDone
    if (offsetMinutes != null && offsetMinutes >= 60) {
      return `${offsetMinutes / 60} ${t.hours}`
    }
    if (offsetMinutes != null && offsetMinutes > 0) {
      return `${offsetMinutes} ${t.minutes}`
    }
    return t.reportBack
  }, [offsetMinutes, t.minutes, t.hours, t.halfway, t.whenDone, t.reportBack])

  const dates = [
    { label: t.startDate, value: from_ts },
    { label: t.endDate, value: deadline },
  ]

  return (
    <div className={styles.summary}>
      <div className={styles.audienceSummary}>
        <Users size={18} aria-hidden="true" />
        <div>
          <span className={styles.summaryLabel}>{t.audience}</span>
          <p dir="auto">{audienceText}</p>
        </div>
      </div>
      <div className={styles.scheduleSummary}>
        {dates.map(({ label, value }) => (
          <div className={styles.summaryItem} key={label}>
            <Calendar size={18} aria-hidden="true" />
            <div>
              <span className={styles.summaryLabel}>{label}</span>
              <p>
                <bdi>{value ? dayjs(value).format('MMM D, YYYY · HH:mm') : '—'}</bdi>
              </p>
            </div>
          </div>
        ))}
        <div className={styles.summaryItem}>
          <Clock size={18} aria-hidden="true" />
          <div>
            <span className={styles.summaryLabel}>{t.reportBack}</span>
            <p>{briefingText}</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TaskSummary
